import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { 
  Building2, 
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Star,
  Archive,
  Menu,
  X,
  Filter,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { API_BASE_URL } from '@/config/api';
import { resolveMediaUrl } from '@/utils/resolveMediaUrl';
import AdminSidebar from '@/components/admin/AdminSidebar';

const AdminProperties = () => {
  const { admin, logout, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filters, setFilters] = useState({
    listing_type: '',
    status: '',
    search: ''
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (!authLoading && !admin) {
      navigate('/admin/login');
    }
  }, [admin, authLoading, navigate]);

  useEffect(() => {
    if (admin) {
      fetchProperties();
    }
  }, [admin, filters]);

  const fetchProperties = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.listing_type) params.append('listing_type', filters.listing_type);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/properties?${params.toString()}`,
        { withCredentials: true }
      );
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (propertyId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/properties/${propertyId}`, {
        withCredentials: true
      });
      setProperties(properties.filter(p => p.id !== propertyId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting property:', error);
    }
  };

  const togglePublish = async (property) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/admin/properties/${property.id}`,
        { is_published: !property.is_published },
        { withCredentials: true }
      );
      setProperties(properties.map(p => 
        p.id === property.id ? { ...p, is_published: !p.is_published } : p
      ));
    } catch (error) {
      console.error('Error updating property:', error);
    }
  };

  const toggleFeatured = async (property) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/admin/properties/${property.id}`,
        { is_featured: !property.is_featured },
        { withCredentials: true }
      );
      setProperties(properties.map(p => 
        p.id === property.id ? { ...p, is_featured: !p.is_featured } : p
      ));
    } catch (error) {
      console.error('Error updating property:', error);
    }
  };

  const archiveProperty = async (property) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/admin/properties/${property.id}`,
        { status: 'archived', is_published: false },
        { withCredentials: true }
      );
      setProperties(properties.map(p =>
        p.id === property.id ? { ...p, status: 'archived', is_published: false } : p
      ));
    } catch (error) {
      console.error('Error archiving property:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const formatPrice = (price, listingType) => {
    const formatted = new Intl.NumberFormat('fr-CH').format(price);
    return listingType === 'rent' ? `CHF ${formatted}/mo` : `CHF ${formatted}`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F5F3EF] flex items-center justify-center">
        <div className="animate-pulse text-[#8B7355]">Loading...</div>
      </div>
    );
  }

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-[#F5F3EF]" data-testid="admin-properties-page">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-[#E0DED8] px-4 py-3 flex items-center justify-between">
        <img src="/byron-logo-transparent-26.svg" alt="Byrōn" className="h-5" />
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <AdminSidebar
          admin={admin}
          onLogout={handleLogout}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          showUserInfo={false}
        />

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-10 min-h-screen">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <h1 className="text-2xl font-heading text-[#1A1A1A]">Properties</h1>
            <Link
              to="/admin/properties/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] text-white text-sm rounded-sm hover:bg-[#333] transition-colors"
              data-testid="add-property-btn"
            >
              <Plus size={16} />
              Add Property
            </Link>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-sm border border-[#E0DED8] p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9B9B9B]" />
                <input
                  type="text"
                  placeholder="Search properties..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355]"
                  data-testid="search-input"
                />
              </div>
              <select
                value={filters.listing_type}
                onChange={(e) => setFilters({ ...filters, listing_type: e.target.value })}
                className="px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm"
                data-testid="listing-type-filter"
              >
                <option value="">All Types</option>
                <option value="sale">For Sale</option>
                <option value="rent">For Rent</option>
              </select>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-4 py-2 border border-[#E0DED8] rounded-sm focus:outline-none focus:border-[#8B7355] text-sm"
                data-testid="status-filter"
              >
                <option value="">All Status</option>
                <option value="available">Available</option>
                <option value="sold">Sold</option>
                <option value="reserved">Reserved</option>
                <option value="rented">Rented</option>
                <option value="off_market">Off Market</option>
              </select>
            </div>
          </div>

          {/* Properties List */}
          <div className="bg-white rounded-sm border border-[#E0DED8] overflow-hidden">
            {properties.length === 0 ? (
              <div className="p-10 text-center text-[#9B9B9B]">
                <Building2 size={40} className="mx-auto mb-3 opacity-30" />
                <p>No properties found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F5F3EF]">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#6B6B6B] font-medium">Property</th>
                      <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#6B6B6B] font-medium">Location</th>
                      <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#6B6B6B] font-medium">Price</th>
                      <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#6B6B6B] font-medium">Type</th>
                      <th className="text-left px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#6B6B6B] font-medium">Status</th>
                      <th className="text-right px-6 py-3 text-xs uppercase tracking-[0.1em] text-[#6B6B6B] font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E0DED8]">
                    {properties.map((property) => (
                      <tr key={property.id} className="hover:bg-[#F5F3EF]/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {property.images?.[0] && (
                              <img 
                                src={resolveMediaUrl(property.images[0])} 
                                alt={property.title}
                                className="w-12 h-12 object-cover rounded-sm"
                              />
                            )}
                            <div>
                              <p className="font-medium text-[#1A1A1A] text-sm">{property.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                {property.is_featured && (
                                  <Star size={12} className="text-[#C6A87C] fill-current" />
                                )}
                                {!property.is_published && (
                                  <span className="text-xs text-[#9B9B9B]">Draft</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-[#6B6B6B]">{property.location}</td>
                        <td className="px-6 py-4 text-sm font-medium text-[#1A1A1A]">
                          {formatPrice(property.price, property.listing_type)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-2 py-1 text-xs rounded ${
                            property.listing_type === 'sale' 
                              ? 'bg-blue-50 text-blue-700' 
                              : 'bg-green-50 text-green-700'
                          }`}>
                            {property.listing_type === 'sale' ? 'Sale' : 'Rent'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-2 py-1 text-xs rounded ${
                            property.status === 'available' ? 'bg-green-50 text-green-700' :
                            property.status === 'sold' ? 'bg-red-50 text-red-700' :
                            property.status === 'reserved' ? 'bg-amber-50 text-amber-700' :
                            property.status === 'rented' ? 'bg-purple-50 text-purple-700' :
                            property.status === 'archived' ? 'bg-gray-100 text-gray-600' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {property.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => toggleFeatured(property)}
                              className={`p-2 rounded hover:bg-[#F5F3EF] transition-colors ${
                                property.is_featured ? 'text-[#C6A87C]' : 'text-[#9B9B9B]'
                              }`}
                              title={property.is_featured ? 'Remove from featured' : 'Add to featured'}
                            >
                              <Star size={16} className={property.is_featured ? 'fill-current' : ''} />
                            </button>
                            <button
                              onClick={() => togglePublish(property)}
                              className={`p-2 rounded hover:bg-[#F5F3EF] transition-colors ${
                                property.is_published ? 'text-green-600' : 'text-[#9B9B9B]'
                              }`}
                              title={property.is_published ? 'Unpublish' : 'Publish'}
                            >
                              {property.is_published ? <Eye size={16} /> : <EyeOff size={16} />}
                            </button>
                            {property.status !== 'archived' && (
                              <button
                                onClick={() => archiveProperty(property)}
                                className="p-2 text-[#6B6B6B] hover:text-orange-600 hover:bg-orange-50 rounded transition-colors"
                                title="Archive"
                              >
                                <Archive size={16} />
                              </button>
                            )}
                            <Link
                              to={`/admin/properties/${property.id}/edit`}
                              className="p-2 text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F5F3EF] rounded transition-colors"
                              title="Edit"
                            >
                              <Edit size={16} />
                            </Link>
                            <button
                              onClick={() => setDeleteConfirm(property.id)}
                              className="p-2 text-[#6B6B6B] hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="font-heading text-lg text-[#1A1A1A] mb-4">Delete Property?</h3>
            <p className="text-sm text-[#6B6B6B] mb-6">
              This action cannot be undone. The property will be permanently removed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-[#E0DED8] text-[#6B6B6B] rounded-sm hover:bg-[#F5F3EF] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminProperties;
